/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Crystal MD force fields — a registry of swappable force-field
 * strategies plus the resolver the engine builder uses.
 *
 * A force field here is NOT a parameter table; it is a STRATEGY. Water,
 * a metal, an ionic salt and a covalent network are different physics
 * (rigid vs flexible, charges or not, virtual sites or not, different
 * pair forms), so each force field is an object that knows how to
 * configure the engine for itself. Adding a new one — TIP4P/2005, an
 * EAM metal potential, a Buckingham ionic potential — is one new entry
 * in CRYSTAL_FORCE_FIELDS; `appliesTo()` scopes it to the crystals it
 * suits, and nothing else in the codebase changes.
 *
 * The user's choice is stored as `CrystalState.forceFieldId` (a stable
 * string, so it persists); `resolveForceField()` turns it back into a
 * strategy, falling back to a sensible default for the composition.
 */
import { CrystalState } from '../../types.ts';
import { Atom } from '../Atom.ts';
import { RadialBond } from '../RadialBond.ts';
import { CrystalDynamics } from './CrystalDynamics.ts';
import { PeriodicBox } from './PeriodicBox.ts';
import { VirtualSite } from './VirtualSite.ts';
import { detectMolecularSpecies } from './forceFields.ts';
import {
  buildAcetyleneConstraints,
  buildAsH3Constraints,
  buildCBr4Constraints,
  buildCCl4Constraints,
  buildCF4Constraints,
  buildCH4Constraints,
  buildCO2Constraints,
  buildCOConstraints,
  buildCS2Constraints,
  buildCyanogenConstraints,
  buildGeH4Constraints,
  buildH2Constraints,
  buildHCNConstraints,
  buildN2Constraints,
  buildN2OConstraints,
  buildNH3Constraints,
  buildP4Constraints,
  buildPH3Constraints,
  buildSF6Constraints,
  buildSiH4Constraints,
  buildWaterConstraints,
} from './RigidConstraints.ts';
import {
  EPM2_CO2,
  EPM2_CS2,
  ETTERS_N2,
  KG_CO,
  KK_N2O,
  OPLS_C2H2,
  OPLS_CH4,
  OPLS_CYANOGEN,
  OPLS_HCN,
  OPLS_NH3,
  SILVERA_H2,
  UFF_ASH3,
  UFF_CBR4,
  UFF_CCL4,
  UFF_CF4,
  UFF_GEH4,
  UFF_P4,
  UFF_PH3,
  UFF_SF6,
  UFF_SIH4,
  applyEpm2CO2ToAtoms,
  applyEpm2CS2ToAtoms,
  applyEttersN2ToAtoms,
  applyKgCOToAtoms,
  applyKkN2OToAtoms,
  applyOplsC2H2ToAtoms,
  applyOplsCH4ToAtoms,
  applyOplsCyanogenToAtoms,
  applyOplsHCNToAtoms,
  applyOplsNH3ToAtoms,
  applySilveraH2ToAtoms,
  applyUffAsH3ToAtoms,
  applyUffCBr4ToAtoms,
  applyUffCCl4ToAtoms,
  applyUffCF4ToAtoms,
  applyUffGeH4ToAtoms,
  applyUffP4ToAtoms,
  applyUffPH3ToAtoms,
  applyUffSF6ToAtoms,
  applyUffSiH4ToAtoms,
} from './forceFields.ts';
import { BuckinghamPotential, InterlayerLJ, MorsePotential, NullPairPotential } from './PairPotential.ts';
import { ionicPackFor } from './buckinghamParameters.ts';
import { metallicPackFor } from './morseParameters.ts';
import { describeEamPack, eamPackFor, normalizeCohesion, zhouPackFor, type EamPack } from './eamParameters.ts';
import { cappedSwPackFor, swPackFor } from './swParameters.ts';
import { cappedTersoffPackFor, tersoffPackFor } from './tersoffParameters.ts';

/**
 * Time-step cap (fs) for rigid water. SHAKE/RATTLE removes the fast O-H
 * stretch a flexible water model carries, so a rigid model is stable
 * well past the ≤0.5 fs flexible water needs — 2 fs is standard for the
 * TIP4P family. Applied everywhere the engine clamps a water time step
 * (this force field, plus the worker and viewer live-update paths).
 */
export const WATER_MAX_TIMESTEP_FS = 2.0;

/** Everything a force field needs to configure a freshly-built engine. */
export interface ForceFieldContext {
  crystal: CrystalState;
  atoms: Atom[];
  /** Intramolecular bonds (empty for atomic crystals). */
  radialBonds: RadialBond[];
  /** The engine shell — the force field mutates it in place. */
  dynamics: CrystalDynamics;
  /** The supercell as a periodic cell, ready for setPeriodicBox(). */
  periodicBox: PeriodicBox;
  /** The user's requested time step (fs); a force field may cap it. */
  requestedTimeStep: number;
}

export interface ForceField {
  /** Stable id stored on CrystalState.forceFieldId. */
  readonly id: string;
  /** Display name for the selector. */
  readonly name: string;
  readonly category: 'water' | 'ionic' | 'metallic' | 'covalent' | 'generic';
  /** Whether this force field is valid for `crystal`'s composition. */
  appliesTo(crystal: CrystalState): boolean;
  /** Set per-atom parameters, virtual sites, bonding mode, PBC, etc. */
  configure(ctx: ForceFieldContext): void;
  /**
   * Optional: the specific potential this field would use for `crystal`
   * and a one-sentence reason to pick it — shown in the force-field picker
   * and reported to the agent. Fields with a single fixed model omit it.
   */
  describe?(crystal: CrystalState): { potential: string; note: string } | null;
  /**
   * Optional: true when this field, although applicable, should NOT be the
   * default for `crystal` — `availableForceFields` lists demoted fields
   * after the rest, and the first listed is the default. Lets a family be
   * the default for most of its elements and step aside for the ones where
   * a measurement showed another pack does better (Zhou vs Finnis–Sinclair
   * for Mo and W).
   */
  demotedFor?(crystal: CrystalState): boolean;
}

// --------------------------------------------------------------------
// Water models. The three share an identical rigid-water setup and
// differ only in their numbers, so one factory builds all three.
//
// LJ epsilon is in eV, converted from the literature kcal/mol values
// (1 eV = 23.0605 kcal/mol). The deeper TIP4P/Ice well is what gives it
// the near-correct ice melting point.
// --------------------------------------------------------------------
interface WaterModel {
  sigmaO: number; // Å
  epsilonO: number; // eV
  chargeH: number; // e
  chargeO: number; // e — 0 for 4-site models (charge is on the M-site)
  fourSite: boolean; // true → negative charge on an off-atom M-site
  chargeM: number; // e — M-site charge (4-site only)
  weightO: number; // M-site = weightO·O + weightH·(H1 + H2)
  weightH: number;
}

/** TIP4P/Ice — best for the ice phases (Abascal et al. 2005). */
const TIP4P_ICE: WaterModel = {
  sigmaO: 3.1668,
  epsilonO: 0.009143, // 0.21084 kcal/mol
  chargeH: 0.5897,
  chargeO: 0,
  fourSite: true,
  chargeM: -1.1794,
  weightO: 0.731,
  weightH: 0.1345,
};

/** TIP4P/2005 — the best general-purpose liquid-water model. */
const TIP4P_2005: WaterModel = {
  sigmaO: 3.1589,
  epsilonO: 0.008031, // 0.1852 kcal/mol
  chargeH: 0.5564,
  chargeO: 0,
  fourSite: true,
  chargeM: -1.1128,
  weightO: 0.7362,
  weightH: 0.1319,
};

/** TIP3P — classic 3-site model; negative charge sits on the oxygen. */
const TIP3P: WaterModel = {
  sigmaO: 3.15061,
  epsilonO: 0.006596, // 0.1521 kcal/mol
  chargeH: 0.417,
  chargeO: -0.834,
  fourSite: false,
  chargeM: 0,
  weightO: 0,
  weightH: 0,
};

/**
 * Build the M-site virtual sites for a 4-site water model: one per
 * oxygen that has exactly two bonded hydrogens, placed at the linear
 * combination weightO·O + weightH·(H1 + H2).
 */
const buildWaterVirtualSites = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom }[],
  weightO: number,
  weightH: number,
  chargeM: number,
): VirtualSite[] => {
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hydrogensOfOxygen = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'O' && ej === 'H') {
      if (!hydrogensOfOxygen.has(i)) hydrogensOfOxygen.set(i, []);
      hydrogensOfOxygen.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'O') {
      if (!hydrogensOfOxygen.has(j)) hydrogensOfOxygen.set(j, []);
      hydrogensOfOxygen.get(j)!.push(i);
    }
  }
  const sites: VirtualSite[] = [];
  for (const [oIdx, hIdxs] of hydrogensOfOxygen) {
    if (hIdxs.length !== 2) continue;
    sites.push(new VirtualSite([atoms[oIdx], atoms[hIdxs[0]], atoms[hIdxs[1]]], [weightO, weightH, weightH], chargeM));
  }
  return sites;
};

/** Factory: a rigid-water force field for one parameter set. */
const makeWaterForceField = (id: string, name: string, model: WaterModel): ForceField => ({
  id,
  name,
  category: 'water',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'water',
  configure: (ctx) => {
    for (const a of ctx.atoms) {
      // Guest molecules carry their own σ/ε/charge from the guest
      // library — the host water field must not overwrite them.
      if (a.guestId !== undefined) continue;
      if (a.elementSymbol === 'O') {
        a.sigma = model.sigmaO;
        a.epsilon = model.epsilonO;
        a.charge = model.chargeO;
      } else if (a.elementSymbol === 'H') {
        // Tiny nonzero LJ on H — avoids a Lorentz-Berthelot singularity.
        a.sigma = 0.4;
        a.epsilon = 0.0001;
        a.charge = model.chargeH;
      }
    }
    if (model.fourSite) {
      ctx.dynamics.virtualSites.push(
        ...buildWaterVirtualSites(ctx.atoms, ctx.radialBonds, model.weightO, model.weightH, model.chargeM),
      );
    }
    // Rigid molecules (SHAKE/RATTLE), periodic boundaries + Ewald.
    ctx.dynamics.constraints = buildWaterConstraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'water';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // Rigid water removes the fast O-H stretch — see WATER_MAX_TIMESTEP_FS.
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, WATER_MAX_TIMESTEP_FS);
  },
});

/**
 * CO₂ (dry ice) force field — EPM2 model (Harris & Yung 1995). Rigid
 * linear triatomic with partial charges, the standard for CO₂ MD.
 * Captures the ~80% of cohesion that comes from the quadrupolar
 * electrostatics (which pure LJ misses entirely).
 *
 *   σ_C = 2.757 Å, ε_C ≈ 2.42 meV; σ_O = 3.033 Å, ε_O ≈ 6.94 meV
 *   q_C = +0.6512 e, q_O = −0.3256 e (rigid C-O = 1.149 Å, O-C-O = 180°)
 *
 * `appliesTo` triggers on a `co2`-detected basis (1 C : 2 O ratio).
 * The supercell builder has already added C-O radial bonds via
 * `computeBonds`; this FF overrides them by replacing flexible bonds
 * with three SHAKE/RATTLE constraints per CO₂ (C-O₁, C-O₂, O₁-O₂),
 * locking the molecule rigid at the loaded geometry. Sublimation
 * enthalpy target: ~0.092 eV/atom (0.276 eV per CO₂ molecule).
 *
 * Periodic boundaries + Ewald: CO₂ has strong electrostatics, so we
 * need the proper Ewald sum, not just DSF. Listed BEFORE the generic
 * LJ FF so dry ice picks it up automatically.
 */
const CO2_FORCE_FIELD: ForceField = {
  id: 'epm2-co2',
  name: 'EPM2 (CO₂)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'co2',
  configure: (ctx) => {
    applyEpm2CO2ToAtoms(ctx.atoms);
    // Rigid CO₂ via SHAKE/RATTLE — replaces the harmonic C-O bonds
    // added by computeBonds. Without rigidity the strong C↔O Coulomb
    // attraction would dissociate the molecule on the first step (the
    // same failure mode that flexible water has).
    ctx.dynamics.constraints = buildCO2Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'co2';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // 2 fs is safe for rigid molecules (no fast intramolecular stretch
    // to integrate); same cap as the rigid water models.
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
    // No `minimize()` call: the EPM2 geometry IS the loaded geometry
    // by construction (constraints pin it), and minimization with
    // strong Coulomb + tight constraints can have convergence issues.
  },
};

/**
 * Methane (solid CH₄) force field — OPLS-AA all-atom (Jorgensen 1996).
 * Rigid tetrahedron with small partial charges. Phase I solid CH₄ is
 * FCC of CH₄ molecules; the molecules are orientationally disordered
 * (rotators) at higher T but the FF treats each as a rigid body
 * (pre-rotational-melting picture).
 *
 *   σ_C = 3.5 Å, ε_C ≈ 2.86 meV; σ_H = 2.5 Å, ε_H ≈ 1.30 meV
 *   q_C = −0.24 e, q_H = +0.06 e
 *   rigid C-H = 1.087 Å, H-C-H = 109.47° (10 distance constraints/molecule)
 *
 * α-CH₄ sublimation enthalpy at 0 K: ~0.099 eV/molecule (~0.020 eV/atom
 * for 5-atom CH₄). OPLS-AA all-atom typically gets within ~10–20%.
 */
const CH4_FORCE_FIELD: ForceField = {
  id: 'opls-ch4',
  name: 'OPLS-AA (CH₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'methane',
  configure: (ctx) => {
    applyOplsCH4ToAtoms(ctx.atoms);
    // 10 constraints per CH₄ — 4 C-H bonds + 6 H-H 1-3 distances. The
    // bond detector adds only the 4 C-H to radialBonds, so the H-H
    // constraints are added inside buildCH4Constraints. Without H-H
    // constraints the tetrahedron could flex into a flat square-planar
    // shape; with them it stays rigid.
    ctx.dynamics.constraints = buildCH4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'methane';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Carbon tetrabromide (solid CBr₄) force field — UFF-style rigid
 * tetrahedral model. Br's large polarizability gives CBr₄ much
 * stronger cohesion than CH₄ despite the same 5-atom shape; the
 * molecular dipole is zero by tetrahedral symmetry, so all cohesion
 * comes from London dispersion + weak Coulomb octupole.
 *
 *   σ_C = 3.43, ε_C ≈ 4.55 meV; σ_Br = 3.73 Å, ε_Br ≈ 10.9 meV
 *   q_C = +0.20, q_Br = −0.05 e
 *   rigid C-Br = 1.94 Å, Br-C-Br = 109.47° (10 distance constraints/molecule)
 *
 * Solid CBr₄ sublimation enthalpy at 0 K: ~0.43 eV/molecule (~0.086
 * eV/atom for 5-atom CBr₄). Real solid CBr₄ Phase I is rotationally
 * disordered ("plastic crystal"); the FF treats each as a rigid body.
 */
const CBR4_FORCE_FIELD: ForceField = {
  id: 'uff-cbr4',
  name: 'UFF (CBr₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'cbr4',
  configure: (ctx) => {
    applyUffCBr4ToAtoms(ctx.atoms);
    // 10 constraints per CBr₄ — 4 C-Br bonds + 6 Br-Br 1-3 distances.
    // Same over-determined-but-correct pattern as CH₄.
    ctx.dynamics.constraints = buildCBr4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'cbr4';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Sulfur hexafluoride (solid SF₆) force field — OPLS/UFF rigid
 * octahedral 7-atom model. First non-tetrahedral / non-pyramidal /
 * non-linear shape: 21 distance constraints per molecule (6 S-F
 * bonds + 15 F-F pairs covering 12 cis at 90° + 3 trans at 180°).
 * The molecule has O_h symmetry so dipole AND quadrupole moments
 * are both zero — only the hexadecapole is non-vanishing, so the
 * very polar S-F bonds (q_S = +1.20) contribute almost nothing to
 * lattice cohesion. Behaves as a "fat noble gas" in the solid.
 *
 *   σ_S = 3.595 Å, ε_S ≈ 11.88 meV; σ_F = 2.997 Å, ε_F ≈ 2.17 meV — both
 *   × 2.19 to reproduce the measured cohesion (see UFF_SF6), no charges
 *   (UFF's +1.20/−0.20 e only repelled at contact; hexadecapole only)
 *   rigid S-F = 1.564 Å, octahedral (21 constraints/molecule)
 *
 * Solid SF₆ Phase II sublimation enthalpy at 0 K: ~0.25 eV/molecule.
 */
const SF6_FORCE_FIELD: ForceField = {
  id: 'uff-sf6',
  name: 'UFF (SF₆)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'sf6',
  configure: (ctx) => {
    applyUffSF6ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildSF6Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'sf6';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * White phosphorus (solid α-P₄) force field — UFF rigid tetrahedron.
 * First all-same-element molecular species in the database: 4 P atoms
 * with every P-P pair as a bond (single bond, 2.21 Å edge). No
 * central atom; no internal partial charges. Cohesion is pure London
 * dispersion on P (large and polarizable, ε_P = 13.2 meV).
 *
 *   σ_P = 3.695 Å, ε_P ≈ 13.2 meV (UFF); q_P = 0
 *   rigid P-P = 2.21 Å (all 6 pairs equivalent; 6 constraints/molecule)
 *
 * Solid α-P₄ sublimation enthalpy at 0 K: ~0.59 eV/molecule.
 * Substantial cohesion driven by P's polarizability and the compact
 * P₄ shape that puts all atoms within range of multiple inter-
 * tetrahedron neighbors.
 */
const P4_FORCE_FIELD: ForceField = {
  id: 'uff-p4',
  name: 'UFF (P₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'p4',
  configure: (ctx) => {
    applyUffP4ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildP4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'p4';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Hydrogen cyanide (solid HCN) force field — OPLS-AA rigid linear
 * 3-atom model. The strongest dipole of any small linear molecule
 * (μ ≈ 2.98 D) drives real-crystal H-bonded chains; in the Pa-3-like
 * preset those chains scatter into four [111]-aligned molecules and
 * the dipole lattice sum cancels by symmetry, leaving LJ alone to
 * provide most of the cohesion the model can reach.
 *
 *   σ_H = 2.50, ε_H ≈ 1.30 meV; σ_C = 3.65, ε_C ≈ 6.50 meV;
 *   σ_N = 3.20 Å, ε_N ≈ 7.37 meV (OPLS-AA nitrile family)
 *   q_H = +0.20, q_C = +0.20, q_N = −0.40 e (large axial dipole)
 *   rigid H-C = 1.064 Å, C-N = 1.156 Å, all atoms linear
 *   3 distance constraints/molecule (H-C bond + C-N bond + H-N pin)
 *
 * Solid HCN sublimation enthalpy at 0 K: ~0.34 eV/molecule.
 */
const HCN_FORCE_FIELD: ForceField = {
  id: 'opls-hcn',
  name: 'OPLS-AA (HCN)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'hcn',
  configure: (ctx) => {
    applyOplsHCNToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildHCNConstraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'hcn';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Cyanogen (solid C₂N₂) force field — OPLS-AA rigid linear 4-atom
 * model. Same chain shape as acetylene with terminal N replacing
 * terminal H; reuses the 6-distance-constraint topology. Cyanogen
 * is centrosymmetric (zero dipole) but has a substantial axial
 * quadrupole that drives the real solid's parallel-aligned packing.
 *
 *   σ_C = 3.30 Å, ε_C ≈ 3.73 meV; σ_N = 3.20 Å, ε_N ≈ 7.37 meV
 *   q_C = +0.20, q_N = −0.20 e (no dipole; quadrupole only)
 *   rigid N≡C = 1.156 Å, C-C = 1.390 Å, all atoms linear
 *   6 distance constraints/molecule (3 bonds + 3 long-range pins)
 *
 * Solid C₂N₂ sublimation enthalpy at 0 K: ~0.32 eV/molecule.
 */
const CYANOGEN_FORCE_FIELD: ForceField = {
  id: 'opls-cyanogen',
  name: 'OPLS-AA (NCCN)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'cyanogen',
  configure: (ctx) => {
    applyOplsCyanogenToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildCyanogenConstraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'cyanogen';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Arsine (solid AsH₃) force field — UFF-style rigid pyramidal model.
 * Group-15 hydride one row heavier than PH₃: narrower H-As-H angle
 * (~91.8°, nearly p-orbital bonding), smaller dipole (~⅓ of PH₃),
 * cohesion dominated by As-As dispersion. Same 6-constraint shape
 * as NH₃/PH₃.
 *
 *   σ_As = 3.77, ε_As ≈ 13.4 meV (UFF As); σ_H = 0.4 (safety)
 *   q_As = −0.15, q_H = +0.05 e (~⅓ of PH₃ charge magnitudes)
 *   rigid As-H = 1.519 Å, H-As-H = 91.8° (6 constraints/molecule)
 *
 * Solid AsH₃ sublimation enthalpy at 0 K: ~0.20 eV/molecule.
 */
const ASH3_FORCE_FIELD: ForceField = {
  id: 'uff-ash3',
  name: 'UFF (AsH₃)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'arsine',
  configure: (ctx) => {
    applyUffAsH3ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildAsH3Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'arsine';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Acetylene (solid C₂H₂) force field — OPLS-AA rigid linear 4-atom
 * model. Unique among hydrocarbons: q_C is NEGATIVE and q_H is
 * POSITIVE (sp hybridization concentrates electron density on C, the
 * OPPOSITE of every other alkane/alkene C-H convention). The large
 * axial quadrupole drives solid acetylene's T-shaped molecular
 * packing and gives substantial "C-H···π" weak hydrogen-bond cohesion.
 *
 *   σ_C = 3.30, ε_C ≈ 3.73 meV; σ_H = 2.42, ε_H ≈ 0.65 meV (OPLS-AA)
 *   q_C = −0.241, q_H = +0.241 e (inverted from methane's sign!)
 *   rigid C≡C = 1.21 Å, C-H = 1.08 Å, all atoms linear
 *   6 distance constraints/molecule (3 bonds + 3 long-range pins)
 *
 * Solid C₂H₂ sublimation enthalpy at 0 K: ~0.24 eV/molecule.
 */
const ACETYLENE_FORCE_FIELD: ForceField = {
  id: 'opls-c2h2',
  name: 'OPLS-AA (C₂H₂)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'acetylene',
  configure: (ctx) => {
    applyOplsC2H2ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildAcetyleneConstraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'acetylene';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Carbon tetrafluoride (solid CF₄) force field — UFF/OPLS rigid
 * tetrahedron. Most-electronegative halogen gives the largest per-bond
 * partial charges of the CX₄ series; molecular dipole still zero by
 * symmetry. The small σ_F makes intermolecular F-F packing comfortable
 * at the experimental lattice constant — unlike CCl₄ and CBr₄, the
 * preset's identical-orientation packing here is NOT geometrically
 * over-constrained.
 *
 *   σ_C = 3.43, ε_C ≈ 4.55 meV; σ_F = 3.00 Å, ε_F ≈ 2.17 meV (UFF)
 *   q_C = +0.48, q_F = −0.12 e (OPLS-AA; the large F charges contribute
 *           a quadrupole/octupole that cancels by Pa-3-like symmetry here)
 *   rigid C-F = 1.32 Å, F-C-F = 109.47° (10 distance constraints/molecule)
 *
 * Solid CF₄ Phase II sublimation enthalpy at 0 K: ~0.18 eV/molecule.
 */
const CF4_FORCE_FIELD: ForceField = {
  id: 'uff-cf4',
  name: 'UFF (CF₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'cf4',
  configure: (ctx) => {
    applyUffCF4ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildCF4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'cf4';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Carbon tetrachloride (solid CCl₄) force field — UFF/OPLS rigid
 * tetrahedron. Sister of CBr₄ — same shape, smaller halogen, slightly
 * weaker LJ on Cl than Br. Real CCl₄ is rotationally disordered at
 * higher T (plastic crystal); the FF treats each molecule as a rigid
 * body, matching the orientationally-ordered low-T limit.
 *
 *   σ_C = 3.43, ε_C ≈ 4.55 meV; σ_Cl = 3.52 Å, ε_Cl ≈ 9.84 meV (UFF)
 *   q_C = +0.248, q_Cl = −0.062 e
 *   rigid C-Cl = 1.766 Å, Cl-C-Cl = 109.47° (10 constraints/molecule)
 *
 * Solid CCl₄ sublimation enthalpy at 0 K: ~0.34 eV/molecule.
 *
 * KNOWN ISSUE — preset geometry: like CBr₄, the SOLID_CCL4 preset puts
 * all four CCl₄ molecules in the same orientation at the high-T
 * plastic-crystal lattice (a = 8.32 Å). At this density the nearest
 * intermolecular Cl-Cl distance falls inside σ_Cl ≈ 3.52, producing
 * strongly positive LJ. The FF parameters are correct; the cohesion
 * result will be wrong-signed at the input geometry. A future preset
 * revision (rotational disorder OR Phase II monoclinic lattice) would
 * unlock correct cohesion without touching the FF.
 */
const CCL4_FORCE_FIELD: ForceField = {
  id: 'uff-ccl4',
  name: 'UFF (CCl₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'ccl4',
  configure: (ctx) => {
    applyUffCCl4ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildCCl4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'ccl4';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Silane (solid SiH₄) force field — UFF-style rigid tetrahedron.
 * Direct heavier-group-14 analog of CH₄ with longer Si-H bonds and
 * deeper LJ on Si. Bond polarity is INVERTED relative to CH₄ (H is
 * more electronegative than Si), so partial charges have opposite
 * sign: q_Si > 0, q_H < 0. Cohesion is mostly Si-Si LJ.
 *
 *   σ_Si = 3.83, ε_Si ≈ 17.4 meV; σ_H = 0.4 (safety), ε_H ≈ 0
 *   q_Si = +0.13, q_H = −0.0325 e
 *   rigid Si-H = 1.48 Å, tetrahedral (10 constraints/molecule)
 *
 * Solid SiH₄ sublimation enthalpy at 0 K: ~0.15 eV/molecule.
 */
const SIH4_FORCE_FIELD: ForceField = {
  id: 'uff-sih4',
  name: 'UFF (SiH₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'silane',
  configure: (ctx) => {
    applyUffSiH4ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildSiH4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'silane';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Germane (solid GeH₄) force field — UFF-style rigid tetrahedron.
 * Heavier still than SiH₄ — same tetrahedral shape, longer Ge-H
 * bonds, slightly smaller partial charges (Ge less polarizing than
 * Si toward H).
 *
 *   σ_Ge = 3.68, ε_Ge ≈ 16.4 meV; σ_H = 0.4 (safety)
 *   q_Ge = +0.10, q_H = −0.025 e
 *   rigid Ge-H = 1.525 Å, tetrahedral (10 constraints/molecule)
 *
 * Solid GeH₄ sublimation enthalpy at 0 K: ~0.15 eV/molecule.
 */
const GEH4_FORCE_FIELD: ForceField = {
  id: 'uff-geh4',
  name: 'UFF (GeH₄)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'germane',
  configure: (ctx) => {
    applyUffGeH4ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildGeH4Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'germane';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Carbon disulfide (solid CS₂) force field — TraPPE-style rigid
 * linear triatomic, same shape as CO₂ but with sulfur. CS₂ is much
 * less polar than CO₂; cohesion is dominated by S-S London dispersion
 * (S is large and very polarizable), not the small quadrupole.
 *
 *   σ_C = 3.20 Å, ε_C ≈ 4.4 meV; σ_S = 3.52 Å, ε_S ≈ 15.8 meV
 *   q_C = +0.154, q_S = −0.077 e (half of EPM2 CO₂; small quadrupole)
 *   rigid C-S = 1.554 Å, S-C-S = 180° (3 constraints/molecule)
 *
 * Solid CS₂ sublimation enthalpy at 0 K: ~0.29 eV/molecule.
 */
const CS2_FORCE_FIELD: ForceField = {
  id: 'trappe-cs2',
  name: 'TraPPE (CS₂)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'cs2',
  configure: (ctx) => {
    applyEpm2CS2ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildCS2Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'cs2';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Nitrous oxide (solid N₂O) force field — three-site rigid linear
 * model. Asymmetric N≡N-O with different bond lengths (1.128 / 1.184
 * Å) but uniform N parameters in the kernel (we can't tell central
 * from terminal N by element). Real N₂O dipole ≈ 0.16 D is approximated
 * away (both N get the same charge); rough quadrupolar field remains.
 *
 *   σ_N = 3.30 Å, ε_N ≈ 3.6 meV; σ_O = 3.10 Å, ε_O ≈ 5.5 meV
 *   q_N = +0.05 each, q_O = −0.10 e (molecule neutral)
 *   rigid r_NN = 1.128 Å, r_NO = 1.184 Å, linear (3 constraints/mol)
 *
 * Solid N₂O sublimation enthalpy at 0 K: ~0.26 eV/molecule.
 */
const N2O_FORCE_FIELD: ForceField = {
  id: 'kk-n2o',
  name: 'Three-site rigid (N₂O)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'n2o',
  configure: (ctx) => {
    applyKkN2OToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildN2OConstraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'n2o';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Solid hydrogen (H₂) force field — Silvera-Goldman isotropic
 * simplification. Real solid H₂ is a quantum solid (zero-point motion
 * comparable to lattice spacing); classical MD with this FF gets
 * QUALITATIVELY the right behavior (very weak vdW solid) but the
 * cohesion magnitude is wrong by ~2× because classical LJ does not
 * account for quantum delocalization. Pedagogical only.
 *
 *   σ_H = 2.96 Å, ε_H ≈ 2.95 meV (Silvera-Goldman reduced)
 *   q_H = 0 (no dipole, no quadrupole for H₂)
 *   rigid H-H = 0.741 Å (1 constraint/molecule)
 *
 * Solid H₂ sublimation enthalpy at 0 K: ~0.0093 eV/molecule (experiment,
 * with quantum effects). Classical FF typically overshoots ~2×.
 */
const H2_FORCE_FIELD: ForceField = {
  id: 'silvera-h2',
  name: 'Silvera-Goldman (H₂)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'h2',
  configure: (ctx) => {
    applySilveraH2ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildH2Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'h2';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // Light atoms → high phonon frequencies. Even rigid H₂ wants a
    // smaller timestep than heavier rigid diatomics; cap at 1 fs.
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 1.0);
  },
};

/**
 * Solid CO force field — Karasawa-Goddard two-site rigid diatomic.
 * CO is N₂'s heteronuclear cousin: similar size, slightly stronger
 * binding from the small dipole (μ ≈ 0.11 D — note the unusual sign,
 * with C on the negative end). Captured here with small partial
 * charges of inverted sign from naïve electronegativity.
 *
 *   σ_C = 3.55 Å, ε_C ≈ 2.4 meV; σ_O = 3.05 Å, ε_O ≈ 4.3 meV
 *   q_C = −0.04, q_O = +0.04 e (C-end negative — empirical sign)
 *   rigid C-O = 1.128 Å (1 constraint/molecule)
 *
 * α-CO sublimation enthalpy at 0 K: ~0.087 eV/molecule.
 */
const CO_FORCE_FIELD: ForceField = {
  id: 'kg-co',
  name: 'Karasawa-Goddard (CO)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'co',
  configure: (ctx) => {
    applyKgCOToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildCOConstraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'co';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Phosphine (solid PH₃) force field — UFF-style rigid pyramidal model.
 * Pyramidal like NH₃ but ~3× weaker dipole (μ ≈ 0.58 D vs 1.47 D),
 * H-P-H much narrower (~93.5° vs 106.7°), no real H-bonding. Cohesion
 * is dominated by London dispersion plus weak dipole-dipole.
 *
 *   σ_P = 3.695 Å, ε_P ≈ 13.2 meV (UFF P); σ_H safety, ε_H ≈ 0
 *   q_P = −0.36 e, q_H = +0.12 e (AM1-BCC-style, ~3× smaller than NH₃)
 *   rigid P-H = 1.42 Å, H-P-H = 93.5° (6 distance constraints/molecule)
 *
 * Solid PH₃ sublimation enthalpy at 0 K: ~0.19 eV/molecule. Same
 * 6-constraint rigid-pyramid construction as NH₃ — only the bonded
 * element and the parameters change.
 */
const PH3_FORCE_FIELD: ForceField = {
  id: 'uff-ph3',
  name: 'UFF (PH₃)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'phosphine',
  configure: (ctx) => {
    applyUffPH3ToAtoms(ctx.atoms);
    ctx.dynamics.constraints = buildPH3Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'phosphine';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Ammonia (solid NH₃) force field — OPLS-AA rigid pyramidal model.
 * Unlike methane, NH₃ has a strong molecular dipole (~1.5 D) because
 * the lone pair breaks the tetrahedral symmetry — most of solid NH₃'s
 * cohesion comes from N-H···N hydrogen bonding (linear dipole-dipole
 * interactions), captured via the large partial charges.
 *
 *   σ_N = 3.42 Å, ε_N ≈ 7.37 meV; σ_H = 0.4 (safety), ε_H ≈ 0
 *   q_N = −1.02 e, q_H = +0.34 e
 *   rigid N-H = 1.012 Å, H-N-H = 106.7° (6 distance constraints/molecule)
 *
 * Solid NH₃ sublimation enthalpy at 0 K: ~0.31 eV/molecule (~0.077
 * eV/atom for 4-atom NH₃). Much stronger cohesion than CH₄ thanks
 * to hydrogen bonding.
 */
const NH3_FORCE_FIELD: ForceField = {
  id: 'opls-nh3',
  name: 'OPLS-AA (NH₃)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'ammonia',
  configure: (ctx) => {
    applyOplsNH3ToAtoms(ctx.atoms);
    // 6 constraints per NH₃ — 3 N-H bonds + 3 H-H 1-3 distances. The
    // bond detector adds only the 3 N-H to radialBonds; the H-H
    // constraints (necessary for full rigidity of the pyramid) are
    // added inside buildNH3Constraints.
    ctx.dynamics.constraints = buildNH3Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'ammonia';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * N₂ (solid nitrogen) force field — Etters (1986) two-site rigid
 * diatomic model. Pure LJ on each N, rigid N-N bond via one SHAKE
 * constraint per molecule. No partial charges — the quadrupolar
 * correction (~25 % of α-N₂ cohesion) is omitted in this simplest
 * form. Suitable for α-N₂ structure and dynamics; quantitative
 * cohesion will be a few % under experiment.
 *
 * α-N₂ sublimation enthalpy at 0 K: ~0.069 eV/molecule (~0.034
 * eV/atom). At the published ε this engine reproduced 61 % of it, so
 * ε is rescaled to match — see ETTERS_N2 in forceFields.ts.
 */
const N2_FORCE_FIELD: ForceField = {
  id: 'etters-n2',
  name: 'Etters (N₂)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) === 'n2',
  configure: (ctx) => {
    applyEttersN2ToAtoms(ctx.atoms);
    // Rigid N-N via SHAKE/RATTLE — one constraint per molecule. With
    // no Coulomb (Etters N₂ has q=0), a flexible model would actually
    // work, but the standard is rigid so we match the literature.
    ctx.dynamics.constraints = buildN2Constraints(ctx.atoms, ctx.radialBonds);
    ctx.dynamics.species = 'n2';
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 2.0);
  },
};

/**
 * Noble gas FCC force field — pure Lennard-Jones with tabulated per-
 * element σ, ε (NOT the element CSV defaults). Used for the four
 * solid-noble-gas presets (Ne, Ar, Kr, Xe). These atomic vdW crystals
 * are the textbook "pure LJ" system; the well-known LJ-Devonshire
 * lattice sum gives FCC cohesion ≈ −8.61·ε per atom.
 *
 * The element CSV stores covalent / van-der-Waals atomic radii and a
 * uniform ε = 0.05 eV default — both wrong for noble-gas LJ. This FF
 * overrides them with the Allen-Tildesley reference values (matching
 * the values already used by the noble-gas guest molecules in
 * crystalGuests.ts):
 *   Ne σ = 2.74 Å,  ε = 36 K  ≈ 3.10 meV
 *   Ar σ = 3.405,   ε = 120 K ≈ 10.3 meV
 *   Kr σ = 3.65,    ε = 164 K ≈ 14.1 meV
 *   Xe σ = 3.98,    ε = 224 K ≈ 19.3 meV
 *   (He is included with σ = 2.56 Å, ε = 11 K, but solid He is a
 *   quantum solid at zero pressure — classical LJ is qualitatively
 *   wrong there. The preset isn't shipped.)
 *
 * Listed BEFORE the generic LJ FF so noble-gas presets default here.
 * Diamond / metals / molecules without a dedicated FF still fall to
 * the generic one.
 */
const KB_EV = 8.617333e-5; // eV/K
const NOBLE_GAS_LJ: Record<string, { sigma: number; epsilon: number }> = {
  He: { sigma: 2.56, epsilon: 10.9 * KB_EV },
  Ne: { sigma: 2.74, epsilon: 36 * KB_EV },
  Ar: { sigma: 3.405, epsilon: 120 * KB_EV },
  Kr: { sigma: 3.65, epsilon: 164 * KB_EV },
  Xe: { sigma: 3.98, epsilon: 224 * KB_EV },
};
const NOBLE_GAS_FORCE_FIELD: ForceField = {
  id: 'lj-noble',
  name: 'Lennard-Jones (noble gas)',
  category: 'generic',
  appliesTo: (crystal) => {
    const elems = new Set(crystal.basis.map((b) => b.element));
    if (elems.size !== 1) return false;
    const e = [...elems][0];
    return e in NOBLE_GAS_LJ;
  },
  configure: (ctx) => {
    for (const a of ctx.atoms) {
      if (a.guestId !== undefined) continue;
      const lj = NOBLE_GAS_LJ[a.elementSymbol];
      if (!lj) continue;
      a.sigma = lj.sigma;
      a.epsilon = lj.epsilon;
      a.charge = 0;
    }
    // Set the kernel cutoff (in σ units) wide enough to capture
    // 2-3 LJ shells, so the FCC cohesion is well-converged. rCutoff
    // is multiplied by per-atom σ internally; 2.5 gives ~8.5 Å for
    // Ar — covering up to 3rd NN. The actual kernel cap is the
    // minimum-image limit, set in setPeriodicBox.
    ctx.dynamics.pairInteractions.rCutoff = 2.5;
    // Periodic boundaries: noble-gas FCC otherwise sees the same
    // surface-attraction collapse as a finite metallic cluster —
    // surface atoms have lopsided LJ wells pulling them inward.
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = ctx.requestedTimeStep;
    // Relax off the textbook FCC lattice onto LJ's own equilibrium
    // (slightly contracted from experimental for the heavier gases).
    ctx.dynamics.minimize();
  },
};

/**
 * Generic Lennard-Jones force field — the fallback for everything that
 * is not water: metals, ionic salts, and other molecular/covalent
 * crystals. Flexible (harmonic) bonds where bonds exist, LJ from the
 * element defaults, formal ionic charges when the crystal carries them.
 */
const GENERIC_FORCE_FIELD: ForceField = {
  id: 'generic',
  name: 'Generic (Lennard-Jones)',
  category: 'generic',
  appliesTo: (crystal) => detectMolecularSpecies(crystal.basis) !== 'water',
  configure: (ctx) => {
    const charges = ctx.crystal.elementCharges;
    if (charges) {
      for (const a of ctx.atoms) {
        // Guest molecules carry their own charge from the guest library.
        if (a.guestId !== undefined) continue;
        a.charge = charges[a.elementSymbol] ?? 0;
      }
    }
    ctx.dynamics.timeStep = ctx.requestedTimeStep;
    // Relax a flexible molecular/covalent crystal off the (possibly
    // imperfect) input geometry before MD; atomic crystals have no bonds.
    if (ctx.radialBonds.length > 0) ctx.dynamics.minimize();
  },
};

/**
 * Ionic force field — Buckingham short-range potential + formal point
 * charges + exact Ewald electrostatics under periodic boundaries.
 *
 * Buckingham reproduces the steep closed-shell-ion repulsion that
 * Lennard-Jones cannot, and Ewald gives the size-independent Madelung
 * energy that a finite cluster's truncated Coulomb sum gets wrong.
 * Parameters are pair-tabulated per composition (buckinghamParameters.ts),
 * so this field `appliesTo` a crystal only once its pack is registered
 * there; unregistered ionic crystals fall through to the generic LJ field.
 */
const IONIC_FORCE_FIELD: ForceField = {
  id: 'buckingham',
  name: 'Buckingham (ionic)',
  category: 'ionic',
  appliesTo: (crystal) => ionicPackFor(crystal) !== null,
  configure: (ctx) => {
    const pack = ionicPackFor(ctx.crystal);
    if (!pack) return; // guarded by appliesTo; defensive.
    // Buckingham reads A/ρ/C from its own pair table and ignores σ/ε; σ
    // survives only as the kernel's neighbor-list cutoff length. Set it
    // uniformly so the short-range cutoff (rCutoff·σ = 2σ) reaches ~8 Å
    // — far past where Born-Mayer + r⁻⁶ decay out — but never exceeds
    // the minimum-image limit (0.49·minWidth): past that limit the kernel
    // clips a pair before the shifted Buckingham potential has reached
    // zero, leaving an energy-conservation discontinuity at the cutoff.
    const sigma = Math.min(4.0, 0.24 * ctx.periodicBox.minWidth);
    for (const a of ctx.atoms) {
      // Guest molecules carry their own parameters from the guest library.
      if (a.guestId !== undefined) continue;
      // Formal charges from the pack — the Buckingham parameters were
      // fitted with exactly these; never mix charge models.
      a.charge = pack.charges[a.elementSymbol] ?? 0;
      a.sigma = sigma;
    }
    ctx.dynamics.pairInteractions.potential = new BuckinghamPotential(pack.pairs);
    // What the framework presents to guest molecules (TraPPE-zeo for silica).
    for (const [el, p] of Object.entries(pack.guestHostLJ ?? {})) {
      ctx.dynamics.pairInteractions.setGuestHostLJ(el, p.sigma, p.epsilonK * KB_EV);
    }
    // Periodic boundaries + Ewald: an ionic lattice has no convergent
    // electrostatic energy as a finite cluster.
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // Recompute the Ewald reciprocal sum every step — no multiple-time-
    // step. The MTS holds the reciprocal-space force constant between
    // refreshes, which is fine when it is a small, slowly-varying
    // correction (water). In an ionic crystal the Coulomb force IS the
    // dominant force; holding it stale is badly non-conservative — a
    // 300 K run heats without bound (past 40,000 K within ~1 ps).
    ctx.dynamics.pairInteractions.setReciprocalStride(1);
    ctx.dynamics.timeStep = ctx.requestedTimeStep;
    // Relax off the textbook lattice geometry onto the Buckingham
    // potential's own minimum before dynamics start — EXCEPT for
    // packs that opt out (e.g. BKS silica, which collapses through
    // an unphysical short-range pathology under minimize). Those
    // packs ship with the preset geometry already at equilibrium.
    if (!pack.skipMinimize) ctx.dynamics.minimize();
  },
};

/**
 * Metallic force field — Morse short-range potential under periodic
 * boundaries. No charges (metals are electrically neutral lattices), so
 * the Ewald path is dormant; the integrator runs flexible velocity-
 * Verlet with the Morse + PBC pair kernel.
 *
 * Morse is a pairwise stand-in for the proper many-body EAM physics
 * that metals genuinely want — it reproduces cohesion and lattice
 * constant when fit to them (Girifalco-Weizer 1959 family) but cannot
 * reproduce the Cauchy-relation-breaking elastic anisotropy of real
 * metals. That is the EAM regime: a ManyBodyPotential interface beyond
 * the strictly pairwise PairPotential we have today.
 *
 * Like the ionic FF this `appliesTo` only crystals with a registered
 * Morse pack (morseParameters.ts), and only metallic crystals
 * (`crystal.metallic` true). Unregistered metallic crystals fall
 * through to the generic LJ field.
 */
const METALLIC_FORCE_FIELD: ForceField = {
  id: 'morse',
  name: 'Morse (metallic)',
  category: 'metallic',
  appliesTo: (crystal) => (crystal.metallic ?? false) && metallicPackFor(crystal) !== null,
  configure: (ctx) => {
    const pack = metallicPackFor(ctx.crystal);
    if (!pack) return; // guarded by appliesTo; defensive.
    // Morse reads D/α/r₀ from its own pair table and ignores σ/ε. σ
    // survives only as the kernel cutoff length, capped at the
    // minimum-image limit so the shifted Morse potential vanishes
    // precisely at the kernel's cut (no truncation discontinuity).
    // Morse decays as exp(−αr): at 2σ ≈ 5 Å the well is essentially
    // closed for all the Girifalco-Weizer metals.
    const sigma = Math.min(2.5, 0.24 * ctx.periodicBox.minWidth);
    for (const a of ctx.atoms) {
      if (a.guestId !== undefined) continue;
      a.sigma = sigma;
      // Metals are electrically neutral — leave atom.charge at its
      // default 0. The Coulomb pass gathers zero charge entities and
      // does no work; Ewald never runs.
    }
    ctx.dynamics.pairInteractions.potential = new MorsePotential(pack.pairs);
    // Periodic boundaries: a metallic supercell otherwise sees the same
    // surface-attraction collapse as a finite ionic cluster — the
    // surface atoms have lopsided neighbor shells and the Morse well
    // pulls them inward.
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    ctx.dynamics.timeStep = ctx.requestedTimeStep;
    // Relax off the textbook lattice onto the Morse potential's own
    // minimum (every Morse parameterization differs slightly from
    // experiment in r_NN).
    ctx.dynamics.minimize();
  },
};

/**
 * EAM force field — embedded-atom-method potential under periodic
 * boundaries. The first implementation is Sutton-Chen (1990), an
 * analytic EAM-family form. Same E = Σ F(ρ) + Σ φ(r) shape as the
 * Daw-Baskes EAM, so the kernel's two-pass density/force path covers
 * both; future Foiles/Mishin EAM packs slot in as additional
 * ManyBodyPotential implementations.
 *
 * Strictly better physics than Morse for metals — EAM gets cohesion
 * without the shifted-force cutoff artifact (because the embedding
 * carries the long-range cohesion, not the pair tail), correct
 * elastic anisotropy (C₁₂ ≠ C₄₄), and the right vacancy and surface
 * energies. Listed BEFORE the Morse FF in the registry so EAM is the
 * default for any metal that has an entry; Morse remains available
 * for comparison.
 */
const configureEam = (ctx: ForceFieldContext, pack: EamPack): void => {
  // EAM-family potentials read their own parameters from the pack and
  // ignore σ/ε on the atom; σ survives only as the kernel cutoff length.
  // HCP needs a longer effective cutoff than FCC/BCC because Cleri-
  // Rosato's slow exponential density decay leaves significant ρ past
  // the default 2σ — bump rCutoff from 2.0 to 2.6 for HCP only, so the
  // distance cutoff is 2.6·σ instead of 2.0·σ at the same σ. FCC/BCC
  // are unaffected (Sutton-Chen's m=6 power law and Finnis-Sinclair's
  // polynomial cutoffs are already well-converged at 2σ).
  const isHcp = Math.abs(ctx.crystal.lattice.gamma - 120) < 1;
  ctx.dynamics.pairInteractions.rCutoff = isHcp ? 2.6 : 2.0;
  const sigma = Math.min(3.5, 0.24 * ctx.periodicBox.minWidth);
  for (const a of ctx.atoms) {
    if (a.guestId !== undefined) continue;
    a.sigma = sigma;
  }
  const potential = pack.build();
  // Cohesion renormalization at THIS cell's cutoff (rCutoff·σ, which
  // shrinks with the cell): packs that declare a target cohesive energy
  // get their ε scaled so the ideal lattice's static energy lands on it.
  // Without this a 3×3 Cu cell (5.2 Å cutoff) binds 12 % weaker than a
  // 4×4 one (7 Å) and melts ~100 K lower for no physical reason.
  normalizeCohesion(potential, pack, ctx.crystal, ctx.dynamics.pairInteractions.rCutoff * sigma);
  ctx.dynamics.pairInteractions.manyBodyPotential = potential;
  // Periodic boundaries — see METALLIC_FORCE_FIELD for the same reasoning.
  ctx.dynamics.setPeriodicBox(ctx.periodicBox);
  // HCP gets a tighter timestep cap. Light, high-cohesion HCP metals
  // (Be especially, then Ti, Zr) have very high phonon frequencies that
  // make dt=1 fs marginal for velocity-Verlet stability — the
  // integrator drifts and the thermostat can't keep up. 0.5 fs is safe
  // across the HCP set without much performance cost.
  ctx.dynamics.timeStep = isHcp ? Math.min(ctx.requestedTimeStep, 0.5) : ctx.requestedTimeStep;
  ctx.dynamics.minimize();
};

/**
 * The classic analytic EAM family: Sutton–Chen (FCC), Finnis–Sinclair
 * (BCC), Cleri–Rosato (HCP), plus the alloy packs. One registry entry
 * covers them all because the composition picks the pack; `describe`
 * names the actual potential so the picker can show it.
 */
const EAM_FORCE_FIELD: ForceField = {
  id: 'eam',
  name: 'EAM — Sutton–Chen / Finnis–Sinclair',
  category: 'metallic',
  appliesTo: (crystal) => (crystal.metallic ?? false) && eamPackFor(crystal) !== null,
  describe: (crystal) => {
    const pack = eamPackFor(crystal);
    return pack ? describeEamPack(pack) : null;
  },
  configure: (ctx) => {
    const pack = eamPackFor(ctx.crystal);
    if (pack) configureEam(ctx, pack);
  },
};

/**
 * Zhou, Johnson & Wadley (2004) EAM. Listed BEFORE the classic entry so it
 * is the default wherever a Zhou pack exists (copper, so far): measured on
 * a copper slab it melts within ~100 K of experiment where Sutton–Chen is
 * ~400 K low — see SUTTON_CHEN_CU_PACK. The classic entry stays available
 * in the picker so the two can be compared on the same crystal.
 */
const EAM_ZHOU_FORCE_FIELD: ForceField = {
  id: 'eam-zhou',
  name: 'EAM — Zhou (2004)',
  category: 'metallic',
  appliesTo: (crystal) => (crystal.metallic ?? false) && zhouPackFor(crystal) !== null,
  // Measured to melt far too high for Mo and W, where Finnis–Sinclair is
  // close — those packs carry `demoted` and the classic field stays default.
  demotedFor: (crystal) => zhouPackFor(crystal)?.demoted === true,
  describe: (crystal) => {
    const pack = zhouPackFor(crystal);
    return pack ? describeEamPack(pack) : null;
  },
  configure: (ctx) => {
    const pack = zhouPackFor(ctx.crystal);
    if (pack) configureEam(ctx, pack);
  },
};

/**
 * Covalent force field — Stillinger-Weber 2-body + 3-body potential under
 * periodic boundaries. The explicit angular term (penalising deviation
 * from the tetrahedral angle 109.47°) is what holds the diamond-cubic
 * structure: no pair potential and no EAM-family embedding can prevent a
 * tetrahedral lattice from relaxing toward close-packed at the first
 * thermal kick. SW reproduces cohesion, lattice constant, elastic
 * constants and the melting point for Si and Ge; adapted SW parameters
 * for diamond carbon and alloy SW for the III-Vs (GaAs, GaP, InP…) are
 * future entries in swParameters.ts.
 *
 * `appliesTo` requires a registered SW pack — the same pattern as the
 * ionic (Buckingham) and metallic (EAM, Morse) force fields. Unregistered
 * covalent crystals fall through to the generic LJ field, where the
 * diamond structure WILL collapse to a denser configuration — SW is not
 * just a nice-to-have for these crystals, it is the physics that makes
 * them stable.
 */
const COVALENT_FORCE_FIELD: ForceField = {
  id: 'sw',
  name: 'Stillinger-Weber (covalent)',
  category: 'covalent',
  // A plain covalent composition, or a hydrogen-terminated finite
  // nanostructure whose heavy elements have a pack (caps on harmonic bonds).
  appliesTo: (crystal) => swPackFor(crystal) !== null || cappedSwPackFor(crystal) !== null,
  describe: (crystal) => {
    if (swPackFor(crystal)) return null;
    const capped = cappedSwPackFor(crystal);
    if (!capped) return null;
    return {
      potential: 'Stillinger-Weber + H caps',
      note:
        'Three-body potential among the heavy atoms (' +
        capped.source +
        '). SW has no hydrogen rows, so each capping hydrogen is held by a harmonic X–H stretch and H–X–Y bends.',
    };
  },
  configure: (ctx) => {
    const direct = swPackFor(ctx.crystal);
    const pack = direct ?? cappedSwPackFor(ctx.crystal);
    if (!pack) return; // guarded by appliesTo; defensive.
    const capped = !direct;
    // SW reads ε/σ/A/B/p/q/a/λ/γ from its own pack; the per-atom σ on
    // ctx.atoms survives only as the kernel's neighbor-list cutoff
    // length. Set it to comfortably exceed the SW pair-and-triple
    // cutoff (a·σ_SW ≈ 3.77 Å for Si) so the kernel walks every triple
    // — the SW cutoff is what truncates, not the kernel σ.
    const tb = pack.build();
    const sigma = Math.min(2.0, 0.24 * ctx.periodicBox.minWidth);
    // The kernel's rCutoff·σ must exceed the SW cutoff so the pair
    // pass reaches every in-cutoff pair. With rCutoff = 2.0 and the σ
    // above, that gives a 4 Å reach — comfortably past Si's 3.77 Å SW
    // cutoff.
    ctx.dynamics.pairInteractions.rCutoff = 2.0;
    for (const a of ctx.atoms) {
      if (a.guestId !== undefined) continue;
      a.sigma = sigma;
      // Covalent crystals are electrically neutral — leave charge at 0.
    }
    ctx.dynamics.pairInteractions.threeBodyPotential = tb;
    // Periodic boundaries — a finite cluster of Si on SW sees the same
    // surface relaxation as a metallic cluster on EAM (under-coordinated
    // surface atoms pull inward and the cluster collapses).
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // X–H stretches (11–17 fs period) need the 0.5 fs cap.
    ctx.dynamics.timeStep = capped ? Math.min(ctx.requestedTimeStep, 0.5) : ctx.requestedTimeStep;
    if (capped && holdCappingHydrogens(ctx.dynamics) === 0) {
      console.warn('capped SW: no X–H bonds found in the scaffold');
    }
    // Relax off the textbook diamond geometry onto the SW potential's
    // own equilibrium (its lattice constant differs from experiment by
    // a few %; running MD without this kick the lattice into compression
    // ringing).
    ctx.dynamics.minimize();
  },
};

/**
 * Tersoff force field — bond-order covalent potential. Covers wider
 * compositions than SW (the carbon allotropes; the III-V semiconductors
 * via alloy mixing; SiC; BN), at the cost of a more complex kernel:
 * the energy of bond (i, j) depends on b_ij, which is itself a sum over
 * every other neighbor k of i. The chain rule for forces reaches every
 * atom in the b_ij sum, not just the (i, j, k) triple.
 *
 * One functional form spans sp³ tetrahedral AND sp² planar bonding —
 * Tersoff's g_i picks its own preferred angle from the parameter set,
 * so the same carbon parameters describe both diamond and graphite
 * without retuning. SW, by contrast, hard-codes the tetrahedral angle.
 *
 * Listed AFTER SW in the registry so Si and Ge default to SW (which
 * was historically fit to those elements); Tersoff is the default for
 * compositions where SW has no pack (most notably C-diamond), and
 * remains user-selectable for Si/Ge from the UI dropdown.
 */
/**
 * Harmonic stretch constants (eV/Å²) for a capping hydrogen on a heavy atom.
 * C–H is the molecular lab's MMFF row; the others follow the X–H stretching
 * frequencies of the hydrides (SiH₄ 2190 cm⁻¹, GeH₄ 2110, PH₃ 2320, AsH₃
 * 2120, GaH₃ / InH₃ 1800–1700). A cap only has to stay put and point along
 * the dangling bond it replaces — the H–X–Y bends do the pointing.
 */
const XH_STRETCH_EV_PER_A2: Record<string, number> = {
  C: 31.8,
  N: 36,
  B: 24,
  Si: 17,
  Ge: 16,
  P: 20,
  As: 16,
  Ga: 12,
  In: 10,
  Al: 12,
  Sb: 13,
};
const XH_STRETCH_DEFAULT_EV_PER_A2 = 15;

/**
 * Hold the capping hydrogens of a hydrogen-terminated nanostructure on
 * harmonic bonds while the bond-order potential runs the heavy atoms (the
 * kernel takes only the elements its parameters cover, so the hydrogens are
 * invisible to it). The scaffold detected every bond; keep the X–H stretches
 * and the bends that involve a hydrogen, drop the heavy–heavy bonds and all
 * torsions — Tersoff IS the heavy-atom bonding and a harmonic bond on top
 * would double-count it. Same recipe the flexible membrane uses for its rim.
 * Returns the number of capping bonds.
 */
const holdCappingHydrogens = (dyn: CrystalDynamics): number => {
  const isH = (x: { elementSymbol: string }) => x.elementSymbol === 'H';
  dyn.radialBonds = dyn.radialBonds.filter((rb) => isH(rb.atom1) !== isH(rb.atom2));
  for (const rb of dyn.radialBonds) {
    const heavy = isH(rb.atom1) ? rb.atom2 : rb.atom1;
    rb.strength = XH_STRETCH_EV_PER_A2[heavy.elementSymbol] ?? XH_STRETCH_DEFAULT_EV_PER_A2;
  }
  dyn.angularBonds = dyn.angularBonds.filter((ab) => isH(ab.atom1) || isH(ab.atom2) || isH(ab.atom3));
  dyn.torsionalBonds = [];
  // The scaffold tagged the whole structure as ONE molecule (its bond graph
  // is connected) and registered every detected bond as an exclusion. Both
  // would hide the heavy–heavy pairs from the SW pair pass. Retag one atom
  // per "molecule", as an atomic crystal is, and exclude only the X–H pairs.
  dyn.atoms.forEach((a, i) => {
    if (a.guestId === undefined) a.moleculeName = 'c' + i;
  });
  dyn.pairInteractions.setBondedPairs(dyn.radialBonds);
  return dyn.radialBonds.length;
};

const TERSOFF_FORCE_FIELD: ForceField = {
  id: 'tersoff',
  name: 'Tersoff (covalent)',
  category: 'covalent',
  // A plain covalent composition, or a hydrogen-terminated finite
  // nanostructure whose heavy elements have a pack (the caps are held by
  // harmonic bonds — see holdCappingHydrogens).
  appliesTo: (crystal) => tersoffPackFor(crystal) !== null || cappedTersoffPackFor(crystal) !== null,
  // Only a pack joined from several fits (a Si/Ge or GaAs/AlAs stack) is
  // named here; a single published fit is just "Tersoff". A capped
  // nanostructure always says so, since the caps are a different model.
  describe: (crystal) => {
    const pack = tersoffPackFor(crystal);
    if (pack) return pack.label ? { potential: pack.label, note: pack.source } : null;
    const capped = cappedTersoffPackFor(crystal);
    if (!capped) return null;
    return {
      potential: `${capped.label ?? 'Tersoff'} + H caps`,
      note: `Bond-order potential among the heavy atoms (${capped.source}). Tersoff has no hydrogen rows, so each capping hydrogen is held by a harmonic X–H stretch and H–X–Y bends.`,
    };
  },
  configure: (ctx) => {
    const direct = tersoffPackFor(ctx.crystal);
    const pack = direct ?? cappedTersoffPackFor(ctx.crystal);
    if (!pack) return; // guarded by appliesTo; defensive.
    const capped = !direct;
    const bo = pack.build();
    // The Tersoff outer cutoff is short — 2.1 Å for C, 3.2 Å for Si,
    // 3.1 Å for Ge — so the kernel σ only needs to give rCutoff·σ a
    // reach that comfortably exceeds it. The bond-order potential's
    // own cutoff (carried on `bo.cutoff`) is what actually truncates;
    // the kernel σ is just the LJ-leftover gating length.
    const sigma = Math.min(2.0, 0.24 * ctx.periodicBox.minWidth);
    ctx.dynamics.pairInteractions.rCutoff = 2.0;
    for (const a of ctx.atoms) {
      if (a.guestId !== undefined) continue;
      a.sigma = sigma;
      // Covalent crystals are electrically neutral — leave charge at 0.
    }
    ctx.dynamics.pairInteractions.bondOrderPotential = bo;
    // Periodic boundaries — finite covalent clusters relax their
    // under-coordinated surface inward (the b_ij of a surface atom is
    // higher because the missing back-side k's leave ζ smaller; the
    // bond pulls harder; the cluster collapses).
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // Carbon's hard repulsion (λ₁ = 3.49 Å⁻¹) and steep cohesion (−7.4
    // eV/atom) push the phonon frequencies high; clamp the timestep
    // for C. Si and Ge are softer and stable at the requested step.
    // A capped structure carries X–H stretches (2000–3000 cm⁻¹, a 11–17 fs
    // period): the same 0.5 fs cap keeps them stable.
    const isHardCovalent = ctx.crystal.basis.some((b) => b.element === 'C');
    ctx.dynamics.timeStep = isHardCovalent || capped ? Math.min(ctx.requestedTimeStep, 0.5) : ctx.requestedTimeStep;
    if (capped) {
      // The scaffold took the molecular branch (its composition key has H, so
      // it did not read as covalent) and detected every bond; keep the caps.
      const caps = holdCappingHydrogens(ctx.dynamics);
      if (caps === 0) console.warn('capped Tersoff: no X–H bonds found in the scaffold');
    }
    ctx.dynamics.minimize();
  },
};

/**
 * Tersoff/SW + interlayer-LJ for LAYERED covalent crystals (graphite,
 * h-BN, MoS₂, …). The in-plane bond-order/three-body potential alone
 * keeps each sheet rigid but has zero long-range vdW — under pure
 * Tersoff or SW the stacked sheets of graphite or 2H-MoS₂ would
 * dissociate in the first thermal kick. This FF runs the in-plane
 * pass AND a switched-LJ overlay (`InterlayerLJ`) that's zero in the
 * bonded region and full-strength past it, contributing the ~0.05–0.1
 * eV/atom interlayer cohesion the bond-order pass cannot.
 *
 * Picks whichever covalent pack applies for the composition: Tersoff
 * for graphite/h-BN, SW for 2H-MoS₂. Listed BEFORE the plain covalent
 * FFs in the registry so any preset carrying `layered: true` defaults
 * to this; non-layered presets (diamond, c-BN, monolayer MoS₂) fall
 * through to plain Tersoff/SW.
 */
const LAYERED_COVALENT_FORCE_FIELD: ForceField = {
  id: 'covalent+lj',
  name: 'Covalent + LJ interlayer (layered)',
  category: 'covalent',
  appliesTo: (crystal) =>
    (crystal.layered ?? false) && (tersoffPackFor(crystal) !== null || swPackFor(crystal) !== null),
  configure: (ctx) => {
    // Pick whichever covalent pack the composition has registered.
    // Tersoff first since it's the broader/older fit; SW only fires for
    // compositions with no Tersoff pack (currently just MoS₂).
    const tersoff = tersoffPackFor(ctx.crystal);
    const sw = swPackFor(ctx.crystal);
    if (!tersoff && !sw) return; // guarded by appliesTo; defensive.
    const sigma = Math.min(2.0, 0.24 * ctx.periodicBox.minWidth);
    ctx.dynamics.pairInteractions.rCutoff = 2.0;
    for (const a of ctx.atoms) {
      if (a.guestId !== undefined) continue;
      a.sigma = sigma;
    }
    if (tersoff) {
      ctx.dynamics.pairInteractions.bondOrderPotential = tersoff.build();
    } else if (sw) {
      ctx.dynamics.pairInteractions.threeBodyPotential = sw.build();
    }
    // Interlayer LJ parameters per element:
    //   C — AIREBO (Stuart 2000): σ = 3.40 Å, ε = 2.84 meV. Standard
    //       graphite interlayer reference.
    //   B, N — UFF (Rappé 1992): B σ = 3.638, ε = 7.81 meV; N σ = 3.263,
    //       ε = 2.99 meV. For h-BN; cross-species via Lorentz-Berthelot
    //       inside InterlayerLJ.
    //   Mo, S — UFF: Mo σ = 2.719, ε = 2.81 meV; S σ = 3.595, ε = 11.88
    //       meV (UFF x = 4.035, D = 0.274 kcal/mol). For 2H-MoS₂
    //       interlayer S-S vdW is the dominant interlayer interaction
    //       since Mo atoms sit inside the trilayers far from the gap.
    //
    // Switching range [3.0, 3.4] chosen to clear:
    //   - graphene in-plane 3rd-NN at 2·a_C-C ≈ 2.842 Å (below 3.0)
    //   - h-BN in-plane 3rd-NN at 2·a_B-N ≈ 2.892 Å (below 3.0)
    //   - MoS₂ in-plane S-S (within a trilayer) at 3.16 Å — just above
    //     rSwitchLow, in the partial-switching region. Not ideal but
    //     workable; full bond-graph switching (AIREBO's approach) would
    //     fix this cleanly. The Mo-S bond at 2.41 Å is well below 3.0
    //     so the bond region itself is untouched.
    // rSwitchHigh = 3.4 covers direct interlayer pairs (~3.35 Å for
    // graphite, ~3.49 Å for MoS₂). Outer cutoff capped at minWidth.
    const interlayer = new InterlayerLJ(
      {
        C: { sigma: 3.4, epsilon: 0.00284 },
        B: { sigma: 3.638, epsilon: 0.00781 },
        N: { sigma: 3.263, epsilon: 0.00299 },
        Mo: { sigma: 2.719, epsilon: 0.00281 },
        S: { sigma: 3.595, epsilon: 0.01188 },
      },
      3.0, // rSwitchLow — clear of in-plane 3rd-NN distances
      3.4, // rSwitchHigh — full LJ above; covers direct interlayer (~3.35 Å)
      Math.min(10.0, 0.49 * ctx.periodicBox.minWidth),
    );
    ctx.dynamics.pairInteractions.interlayerLJ = interlayer;
    ctx.dynamics.setPeriodicBox(ctx.periodicBox);
    // Carbon's high phonon frequencies cap the timestep at 0.5 fs;
    // MoS₂ and similar TMDs are softer but the same cap is safe.
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 0.5);
    ctx.dynamics.minimize();
  },
};

/** The registry — ordered so the first applicable entry is the default. */
/**
 * Membrane force field — a pinned graphene sheet partitioning a gas cell
 * (NanostructureSpec.membrane). The sheet is rigid: every framework atom is
 * fixed and the primary potential is null, so no framework forces are
 * computed. Gas molecules feel the sheet and each other through the guest
 * overlay, with the SAME gas–surface Lennard-Jones the free-energy tool
 * (computeMembranePermeation) uses — Steele graphite carbon σ 3.4 Å,
 * ε/k 28 K; OPLS aromatic hydrogen σ 2.42 Å, ε/k 15.1 K on a capped rim —
 * so the crossings a run counts and the barriers the tool computes are one
 * model. No periodic box: the container is the cell, its reflecting walls
 * meet the sheet's edges, and gas crosses only through the pore.
 */
/**
 * The FLEXIBLE membrane: carbon–carbon under Tersoff (the kernel takes only the
 * atoms its parameters cover), each rim hydrogen on a harmonic C–H bond plus
 * the H–C–C angles that keep it in the sheet plane (the scaffold's harmonic
 * C–C bonds, angles and torsions are dropped — Tersoff IS the carbon bonding).
 * Periodic sideways; a frame of carbons along the cell border is pinned as the
 * support the sheet is suspended from, which also stops the whole sheet
 * drifting up or down; reflecting planes at z = ±halfHeight close the chambers.
 */
const configureFlexibleMembrane = (ctx: ForceFieldContext, halfHeight: number): void => {
  const pack = tersoffPackFor({ ...ctx.crystal, basis: [{ element: 'C', fx: 0, fy: 0, fz: 0, label: 'C1' }] });
  if (!pack) return;
  const dyn = ctx.dynamics;
  dyn.pairInteractions.bondOrderPotential = pack.build();
  const box = ctx.periodicBox;
  const [A, B] = box.latticeVectors();
  const halfX = A.length() / 2;
  const halfY = B.length() / 2;
  let pinned = 0;
  for (const a of ctx.atoms) {
    if (a.guestId !== undefined) continue;
    a.charge = 0;
    a.sigma = 1; // neighbor-list length only; the overlay carries gas–sheet LJ
    // The support: the first row of carbons inside the cell's -x and -y faces
    // (the cell is periodic, so these rows ARE the frame around every pore).
    if (a.elementSymbol === 'C' && (a.position.x < -halfX + 0.8 || a.position.y < -halfY + 0.8)) {
      a.fixed = true;
      pinned++;
    }
  }
  const isH = (x: { elementSymbol: string }) => x.elementSymbol === 'H';
  dyn.radialBonds = dyn.radialBonds.filter((rb) => isH(rb.atom1) || isH(rb.atom2));
  dyn.angularBonds = dyn.angularBonds.filter((ab) => isH(ab.atom1) || isH(ab.atom2) || isH(ab.atom3));
  dyn.torsionalBonds = [];
  dyn.pairInteractions.potential = new NullPairPotential();
  dyn.pairInteractions.setGuestHostLJ('C', 3.4, 28 * KB_EV);
  dyn.pairInteractions.setGuestHostLJ('H', 2.42, 15.1 * KB_EV);
  dyn.setPeriodicBox(box);
  dyn.zWallHalf = halfHeight;
  dyn.countMovables();
  // 1 fs, not the Tersoff force field's 0.5 fs cap: that cap guards hot
  // diamond and carbon melts. A room-temperature membrane conserves energy at
  // 1 fs (NVE, 5 ps, 420 atoms: ΔE within ±0.04 eV of ~15 eV kinetic, no drift,
  // 2026-10-18) and runs twice as fast.
  dyn.timeStep = Math.min(ctx.requestedTimeStep, 1);
  if (pinned === 0) console.warn('flexible membrane: no support atoms pinned');
};

const MEMBRANE_FORCE_FIELD: ForceField = {
  id: 'membrane',
  name: 'Rigid membrane (gas–surface LJ)',
  category: 'generic',
  appliesTo: (crystal) => crystal.nanostructure?.membrane === true,
  configure: (ctx) => {
    const spec = ctx.crystal.nanostructure;
    if (spec?.membraneFlexible) {
      configureFlexibleMembrane(ctx, spec.chamberHalfAng ?? ctx.crystal.lattice.c / 2);
      return;
    }
    for (const a of ctx.atoms) {
      if (a.guestId !== undefined) continue;
      a.fixed = true;
      a.charge = 0;
    }
    ctx.dynamics.pairInteractions.potential = new NullPairPotential();
    ctx.dynamics.pairInteractions.setGuestHostLJ('C', 3.4, 28 * KB_EV);
    ctx.dynamics.pairInteractions.setGuestHostLJ('H', 2.42, 15.1 * KB_EV);
    ctx.dynamics.countMovables();
    // Hydrogen (2 amu) is the fastest thing in the cell; 1 fs keeps it stable.
    ctx.dynamics.timeStep = Math.min(ctx.requestedTimeStep, 1);
  },
};

export const CRYSTAL_FORCE_FIELDS: ForceField[] = [
  MEMBRANE_FORCE_FIELD,
  makeWaterForceField('tip4p-ice', 'TIP4P/Ice', TIP4P_ICE),
  makeWaterForceField('tip4p-2005', 'TIP4P/2005', TIP4P_2005),
  makeWaterForceField('tip3p', 'TIP3P', TIP3P),
  IONIC_FORCE_FIELD,
  EAM_ZHOU_FORCE_FIELD,
  EAM_FORCE_FIELD,
  METALLIC_FORCE_FIELD,
  LAYERED_COVALENT_FORCE_FIELD,
  NOBLE_GAS_FORCE_FIELD,
  CO2_FORCE_FIELD,
  N2_FORCE_FIELD,
  CH4_FORCE_FIELD,
  NH3_FORCE_FIELD,
  PH3_FORCE_FIELD,
  CBR4_FORCE_FIELD,
  CF4_FORCE_FIELD,
  CCL4_FORCE_FIELD,
  ASH3_FORCE_FIELD,
  ACETYLENE_FORCE_FIELD,
  HCN_FORCE_FIELD,
  CYANOGEN_FORCE_FIELD,
  SF6_FORCE_FIELD,
  P4_FORCE_FIELD,
  SIH4_FORCE_FIELD,
  GEH4_FORCE_FIELD,
  CS2_FORCE_FIELD,
  N2O_FORCE_FIELD,
  H2_FORCE_FIELD,
  CO_FORCE_FIELD,
  COVALENT_FORCE_FIELD,
  TERSOFF_FORCE_FIELD,
  GENERIC_FORCE_FIELD,
];

/** Force fields valid for a crystal's composition — for the UI selector. */
export const availableForceFields = (crystal: CrystalState): ForceField[] => {
  const list = CRYSTAL_FORCE_FIELDS.filter((ff) => ff.appliesTo(crystal));
  // Registry order, except that a field demoted for THIS crystal moves to just
  // after the last other field of its category — behind the pack that beat
  // it, but still ahead of the generic fallbacks.
  for (const ff of list.filter((f) => f.demotedFor?.(crystal))) {
    list.splice(list.indexOf(ff), 1);
    let at = -1;
    for (let i = 0; i < list.length; i++) if (list[i].category === ff.category) at = i;
    list.splice(at + 1, 0, ff);
  }
  return list;
};

/**
 * True when a force field fit for this composition exists — anything but
 * the generic Lennard-Jones fallback. The lab runs any plain crystal, on the
 * fallback if need be; a device (a layered stack, see deviceBuilder.ts) is
 * only simulated when a real fit covers all its layers together: a
 * mismatched interface on generic LJ was judged not worth showing. A Si/Ge
 * stack, III-V stacks whose binaries are all registered, and cross-family
 * stacks whose interface pairs can be mixed from real pure-element rows
 * (GaAs on Si, Ge on GaAs, GaN on SiC — a labeled proxy) qualify
 * (tersoffParameters.ts); InP on Si, with no pure In row to mix, does not.
 */
export const hasDedicatedForceField = (crystal: CrystalState): boolean =>
  availableForceFields(crystal).some((ff) => ff.category !== 'generic');

/**
 * Resolve a crystal's force field: the user's choice if it is set and
 * still applies, otherwise the default (first applicable) for the
 * composition.
 */
export const resolveForceField = (crystal: CrystalState): ForceField => {
  if (crystal.forceFieldId) {
    const chosen = CRYSTAL_FORCE_FIELDS.find((ff) => ff.id === crystal.forceFieldId);
    if (chosen && chosen.appliesTo(crystal)) return chosen;
  }
  return availableForceFields(crystal)[0] ?? GENERIC_FORCE_FIELD;
};
